import { StateCreator, createStore } from "zustand";
import { SessionStore, createSessionStore } from "./session/store";
import { enableMapSet } from "immer";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStore as zustandUseStore } from "zustand";
import { mergeDeepLeft } from "ramda";
import { createDialogStore, DialogStore } from "./dialog/store";
import { ChatStore, createChatStore } from "./chat/store";
import { WorkoutSessionStore, createWorkoutStore } from "./workout/store";
import { CheckinsStore, createCheckinsStore } from "./checkins/store";
import { sectionsForSession } from "@trainrr/api/workout-sections";

enableMapSet();

export type State = SessionStore &
  DialogStore &
  ChatStore &
  WorkoutSessionStore &
  CheckinsStore;

export type CombinedStateCreator<T> = StateCreator<
  State,
  [
    ["zustand/devtools", never],
    ["zustand/immer", never],
    ["zustand/subscribeWithSelector", never],
    ["zustand/persist", unknown],
  ],
  [],
  T
>;

const stateCreators: CombinedStateCreator<State> = (...a) => ({
  ...createSessionStore(...a),
  ...createDialogStore(...a),
  ...createChatStore(...a),
  ...createWorkoutStore(...a),
  ...createCheckinsStore(...a),
});

export const store = createStore(
  devtools(
    immer(
      subscribeWithSelector(
        persist(stateCreators, {
          name: "zustand-global-store",
          merge: (persistedState: any, currentState) => {
            const merged = mergeDeepLeft(persistedState, currentState);
            if (merged?.workoutSession?.sectionClocks) {
              const { sectionClocks: _clocks, ...workoutSession } =
                merged.workoutSession;
              merged.workoutSession = workoutSession;
            }
            return merged;
          },
          onRehydrateStorage: () => (state) => {
            if (state?.session) {
              // Mark session as needing refresh after rehydrate
              state.session.needsRefreshAfterRehydrate = true;
              state.session.isSwitchingOrg = false;
            }

            const activeWorkout = state?.workoutSession?.activeWorkoutData;
            if (activeWorkout?.startTime) {
              activeWorkout.startTime = new Date(activeWorkout.startTime);
            }
            if (activeWorkout?.endTime) {
              activeWorkout.endTime = new Date(activeWorkout.endTime);
            }
            if (activeWorkout?.exercises) {
              activeWorkout.sections = sectionsForSession(
                activeWorkout.sections,
                activeWorkout.exercises,
              );
            }
            const restTimer = state?.workoutSession?.restTimer;
            if (restTimer?.running) {
              const lastTickAt =
                typeof restTimer.lastTickAt === "number"
                  ? restTimer.lastTickAt
                  : Date.now();
              const elapsedSeconds = Math.floor(
                (Date.now() - lastTickAt) / 1000,
              );
              if (elapsedSeconds > 0) {
                const remaining = Math.max(
                  0,
                  restTimer.remaining - elapsedSeconds,
                );
                restTimer.remaining = remaining;
                restTimer.running = remaining > 0;
                restTimer.lastTickAt = remaining > 0 ? Date.now() : undefined;
              } else if (!restTimer.lastTickAt) {
                restTimer.lastTickAt = Date.now();
              }
            }
          },
          partialize: (state) => ({
            session: {
              ...state.session,
              isSwitchingOrg: false,
            },
            chatDrafts: state.chatDrafts,
            workoutSession: {
              activeWorkoutData: state.workoutSession.activeWorkoutData,
              minimized: state.workoutSession.minimized,
              restDefaultSeconds: state.workoutSession.restDefaultSeconds,
              autoStartRestTimer: state.workoutSession.autoStartRestTimer,
              restTimer: state.workoutSession.restTimer,
            },
            coachAppDialog: { hasUnread: state.coachAppDialog.hasUnread },
            checkins: state.checkins,
          }),
        }),
      ),
    ),
  ),
);

export const useStore = <U>(
  selector: (state: State) => U,
  equalityFn?: (a: U, b: U) => boolean,
) => zustandUseStore(store, selector, equalityFn);
